import React from 'react';
import { FaDesktop } from 'react-icons/fa6';
import { DEFAULT_EMPTY_MESSAGE, type Column } from './DataTable';
import { deNumeral } from './plural-ro';

/**
 * Rezumatul de raport pentru mobil — decizia owner-ului, 2026-09-03
 *
 * Sub `md` (768px) **nu se randeaza tabel deloc**: nici tabelul, nici
 * card-per-rand, nici filtrele pe coloana, nici exportul Excel. Rapoartele sunt
 * unelte de birou, iar 51 de coloane pe un ecran de 360px nu devin utilizabile
 * prin rearanjare — devin doar mai lungi. Ce ramane pe telefon e informatia
 * generala: cate randuri sunt, primele trei, si unde se vede restul.
 *
 * Macheta: `docs/design-system/mockups/redesign-2026-09/b-raport.html` (sectiunea MOBIL).
 *
 * ⚠️ Componenta asta NU se randeaza niciodata pe desktop — `ReportPageLayout` o
 * pune sub `md:hidden`. Orice control care trebuie sa existe si pe desktop NU
 * are ce cauta aici.
 */

export interface ReportSummaryStat {
    /** Eticheta scurta (majusculele vin din CSS, nu din text) */
    label: string;
    /** Valoarea deja formatata (`toLocaleString('ro-RO')` la apelant) */
    value: React.ReactNode;
    /** `accent` = verde brand (cifra principala), `warning` = rosu (lipsuri) */
    tone?: 'default' | 'accent' | 'warning';
}

export interface ReportMobileSummaryProps<T> {
    columns: Column<T>[];
    data: T[];
    totalElements: number;
    /** Numele entitatii la plural, pentru totalul implicit: „scoli", „utilizatori" */
    entityLabel?: string;
    /** Cifrele de sus. Fara ele se afiseaza doar totalul de randuri. */
    stats?: ReportSummaryStat[];
    /** Unitatea cifrei din dreapta randului („kg", „elevi"). Constanta raportului. */
    unitLabel?: string;
    loading?: boolean;
    /** Textul starii de gol. Implicit `DEFAULT_EMPTY_MESSAGE` (comun cu `DataTable`). */
    emptyMessage?: string;
}

/**
 * Coloana care da textul randului in rezumat.
 *
 * Fara `cardRole: 'title'` explicit, se ia prima coloana **de date**, nu
 * `columns[0]`: prima poate fi o coloana de actiuni conditionata de rol (vezi
 * `preview` din `EditionStatusReportPage`, vizibila doar la ADMIN/DBEST), iar un
 * index fix ar pune un buton in locul numelui.
 */
function coloanaTitlu<T>(columns: Column<T>[]): Column<T> | undefined {
    return (
        columns.find((c) => c.cardRole === 'title') ??
        columns.find((c) => c.sortable !== false && c.filterable !== false) ??
        columns[0]
    );
}

function valoareCelula<T>(column: Column<T> | undefined, row: T): React.ReactNode {
    if (!column) return null;
    const brut = (row as Record<string, any>)[column.key];
    // `cardRender` bate `render`: vezi comentariul de la campul din `DataTable`.
    // Un render de celula poate intoarce un control, iar cardul asta e pasiv.
    const randeaza = column.cardRender ?? column.render;
    return randeaza ? randeaza(brut, row) : (brut ?? '—');
}

/** Randul chiar are cifra? `null`, `undefined` si marcajele de gol nu se pun la socoteala. */
function areValoare<T>(column: Column<T> | undefined, row: T): boolean {
    if (!column) return false;
    const brut = (row as Record<string, any>)[column.key];
    if (brut === null || brut === undefined || brut === '') return false;
    const afisat = String(valoareCelula(column, row) ?? '').trim();
    return afisat !== '' && afisat !== '-' && afisat !== '—';
}

export function ReportMobileSummary<T extends Record<string, any>>({
    columns,
    data,
    totalElements,
    entityLabel = 'rezultate',
    stats,
    unitLabel,
    loading,
    emptyMessage
}: ReportMobileSummaryProps<T>) {
    const colTitlu = React.useMemo(() => coloanaTitlu(columns), [columns]);
    const colValoare = React.useMemo(() => columns.find((c) => c.cardRole === 'value'), [columns]);

    const primele = data.slice(0, 3);

    const cifre: ReportSummaryStat[] = stats?.length
        ? stats
        : [{ label: 'Total ' + entityLabel, value: totalElements.toLocaleString('ro-RO'), tone: 'accent' }];

    return (
        <section className="flex flex-col gap-6" aria-label="Rezumatul raportului">
            {/* Cifrele generale */}
            <ul className="grid grid-cols-2 gap-3 list-none p-0 m-0">
                {cifre.map((cifra, i) => (
                    <li key={i} className="ecotic-report-card flex flex-col gap-1 px-4 py-3">
                        <span className="text-very-small font-bold uppercase tracking-wider text-gray-600">
                            {cifra.label}
                        </span>
                        <span
                            className={[
                                'font-heading text-very-large leading-tight tabular-nums',
                                cifra.tone === 'accent'
                                    ? 'text-magenta'
                                    : cifra.tone === 'warning'
                                      ? 'text-error'
                                      : 'text-dark'
                            ].join(' ')}
                        >
                            {cifra.value}
                        </span>
                    </li>
                ))}
            </ul>

            {/* Primele trei randuri, ca informatie generala — nu ca tabel */}
            {loading ? (
                <p className="text-small text-gray-500 m-0">Se încarcă…</p>
            ) : primele.length > 0 ? (
                <div className="flex flex-col gap-2">
                    <h2 className="font-heading text-smaller font-semibold uppercase tracking-wider text-gray-500 m-0">
                        Primele {primele.length} din {totalElements.toLocaleString('ro-RO')}
                    </h2>
                    <ol className="flex flex-col gap-3 list-none p-0 m-0">
                        {primele.map((rand, index) => (
                            <li key={index} className="ecotic-report-card flex items-center gap-3 px-4 py-3">
                                <span
                                    aria-hidden="true"
                                    className="flex-none grid place-items-center size-7 rounded-full bg-lilac-light font-heading text-small font-semibold text-magenta-hover"
                                >
                                    {index + 1}
                                </span>
                                <span className="flex-1 min-w-0 text-small font-semibold text-dark break-words">
                                    {valoareCelula(colTitlu, rand)}
                                </span>
                                {colValoare && (
                                    <span className="flex-none font-heading text-large font-semibold text-magenta tabular-nums">
                                        {valoareCelula(colValoare, rand)}
                                        {/* Unitatea apare doar cand exista o cifra: „— kg" nu inseamna nimic. */}
                                        {unitLabel && areValoare(colValoare, rand) && (
                                            <span className="font-body text-very-small font-bold uppercase tracking-wide text-gray-600 ml-1">
                                                {unitLabel}
                                            </span>
                                        )}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ol>
                </div>
            ) : (
                <p className="text-small text-gray-500 m-0">{emptyMessage ?? DEFAULT_EMPTY_MESSAGE}</p>
            )}

            {/* Trimiterea la desktop. Numarul de coloane e cel real, nu unul scris de mana. */}
            <div className="ecotic-report-card flex items-start gap-3 px-4 py-4">
                {/* `bg-white`, nu `bg-light`: `--color-light` si `--color-muted` (fundalul
                    cardului, de la #61) sunt AMANDOUA `rgb(218 231 214)`, deci placuta se
                    picta in culoarea cardului — masurat 1,00, adica obiectul nu exista.
                    Nu e un prag WCAG: placuta e `aria-hidden` si pictograma citeste 5,77
                    si fara ea. E `size-11` care da pictogramei o amprenta de 44px, ca
                    randul sa se alinieze cu blocul de doua randuri de text de langa.
                    Albul da 1,28 — exact delta pe care cardul o are fata de panou, deci
                    citeste ca acelasi fel de obiect, un nivel mai jos. */}
                <span aria-hidden="true" className="flex-none grid place-items-center size-11 rounded-lg bg-white">
                    <FaDesktop className="size-5 text-magenta" />
                </span>
                <div className="flex flex-col gap-1">
                    <h2 className="font-heading text-large font-semibold text-dark m-0">
                        Tabelul complet e pe calculator
                    </h2>
                    <p className="text-small text-gray-600 m-0">
                        Cele <strong className="text-dark">{columns.length} {deNumeral(columns.length)}coloane</strong>, filtrele pe
                        coloană și exportul în Excel se folosesc de pe desktop. Aici vezi doar cifrele
                        generale ale raportului.
                    </p>
                </div>
            </div>
        </section>
    );
}
